import React, { useCallback } from 'react';
import styled from 'styled-components';

import { isDefined } from '../../../../../../utils/variables';
import { Poi } from '../../../../../../viewer/elements/poi/poi';
import { publish, SubscriptionTopic } from '../../../../../../viewer/messaging/pubsub';
import { SceneManager } from '../../../../../../viewer/scene-manager';
import { useDialog } from '../../../../../hooks/use-dialog';
import { useSharedView } from '../../../../../hooks/use-shared-view';
import { useSubscribe } from '../../../../../hooks/use-subscribe';
import { Icon } from '../../../../icon/icon';
import { MoveIcon } from '../../../../skyview/right-menu/components/move-icon';
import { Stack } from '../../../../stack/stack';
import { useSubscribeShowDeleteAssetDialog } from '../../../hooks/subscribe-show-delete-asset-dialog';
import { AssetHeader } from '../../components/asset-blocks/asset-header';
import { DeleteAssetDialog } from '../../dialogs/delete-asset-dialog';
import { PoiDialog } from '../../dialogs/poi-dialog/poi-dialog';
import { PoiImageDialog } from '../../dialogs/poi-dialog/poi-image-dialog';
import { DeleteIcon } from '../../icons/delete-icon';
import { EditIcon } from '../../icons/edit-icon';
import { PanoramaIcon } from '../../icons/panorama-icon';

type Props = {
  asset: Poi;
  refreshAsset: () => void;
};

const PoiHeader = (props: Props) => {
  const editDialog = useDialog();
  const imageDialog = useDialog();
  const deleteDialog = useDialog();
  const { isSharedView } = useSharedView();
  useSubscribeShowDeleteAssetDialog(props.asset, deleteDialog, isSharedView);

  const resolveDisplayingImageDialog = (assetId?: string) => {
    const isPanoramaInPanoramaView =
      SceneManager.instance.isSceneActive('panoramaViewer') && props.asset.is360Poi();
    if (isPanoramaInPanoramaView) {
      return;
    }
    if (assetId === props.asset.assetId && isDefined(props.asset.getImagePreviewUrl())) {
      imageDialog.show();
    }
  };

  useSubscribe(SubscriptionTopic.AssetSelectedInViewer, (message) => {
    resolveDisplayingImageDialog(message.assetId);
  });
  useSubscribe(SubscriptionTopic.OpenPoiPreviewDialog, (message) => {
    resolveDisplayingImageDialog(message.assetId);
  });

  const getPoiTitlePrefix = useCallback(() => {
    const thumbnailUrl = props.asset.getThumbnailResourceUrl('64x_');
    const clickableIcon = thumbnailUrl || !isSharedView;
    return (
      <PoiPrefix
        alignItems="center"
        justifyContent="center"
        onClick={
          clickableIcon
            ? (event) => {
                imageDialog.show();
                event?.stopPropagation();
              }
            : undefined
        }
      >
        {isDefined(thumbnailUrl) ? (
          <PoiThumbnail src={thumbnailUrl} />
        ) : (
          <Icon icon={['fad', 'location-dot']} size="lg" />
        )}
      </PoiPrefix>
    );
  }, [props.asset, isSharedView, imageDialog]);

  const resolveAdditionalHeaderIcons = () => {
    const additionalHeaders = [
      <EditIcon
        key="editIcon"
        onClick={() => {
          editDialog.show();
        }}
      />,
      <MoveIcon asset={props.asset} key="moveIcon" />,
      <DeleteIcon
        key="deleteIcon"
        onClick={(e) => deleteDialog.show({ x: e.clientX, y: e.clientY })}
      />,
    ];

    if (props.asset.is360Poi()) {
      additionalHeaders.push(
        <PanoramaIcon
          key="panoramaIcon"
          onClick={async (e) => {
            await SceneManager.instance.setScene('panoramaViewer');
            publish(SubscriptionTopic.AssetChanged, props.asset);
          }}
        />,
      );
    }

    return additionalHeaders;
  };

  return (
    <>
      <AssetHeader
        additionalIcons={isSharedView ? undefined : resolveAdditionalHeaderIcons()}
        asset={props.asset}
        name={props.asset.name}
        namePrefix={getPoiTitlePrefix()}
      />

      {editDialog.render(
        <PoiDialog
          context={{
            type: 'EXISTING',
            poi: props.asset,
          }}
          onClose={(saved) => {
            editDialog.hide();

            if (saved) {
              props.refreshAsset();
            }
          }}
        />,
      )}

      {imageDialog.render(
        <PoiImageDialog
          poi={props.asset}
          onClose={() => {
            imageDialog.hide();
          }}
        />,
      )}

      {deleteDialog.render((pos) => (
        <DeleteAssetDialog asset={props.asset} pos={pos} onClose={deleteDialog.hide} />
      ))}
    </>
  );
};

const PoiPrefix = styled(Stack)`
  width: 3em;
  height: 3em;
`;

const PoiThumbnail = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

export { PoiHeader, Props as PoiHeaderProps };
